import React, { useEffect, useState, useRef } from 'react';
import {
  getNotices,
  deleteNotice,
  createNotice,
  updateNotice,
  sendNotice
} from '../../api/services/noticeApi';
import { useData } from '../../contexts/DataContext';
import { unitApi } from '../../api/services/unitApi';
import { useNavigate } from 'react-router-dom';
import {
  Plus, Bell, Edit, Trash2, Eye, ChevronDown, MessageCircle, Send, Clock, CheckCircle2, Search, Filter, Calendar, Users, X, FileText, Smartphone, Pin, RefreshCw, MoreVertical, MapPin, Loader, Info, AlertCircle, BookOpen, CalendarOff
} from 'lucide-react';
import toast from 'react-hot-toast';
import ConfirmationModal from '../../ui/ConfirmationModal';
import LoadingOverlay from '../../ui/LoadingOverlay';
import BrandedButton from '../../ui/BrandedButton';

// --- Premium UI Components ---

const StatCard = ({ icon: Icon, label, value, color }) => {
  const colorMap = {
    blue: 'from-blue-500/10 to-blue-600/5 text-blue-600 border-blue-100',
    green: 'from-emerald-500/10 to-emerald-600/5 text-emerald-600 border-emerald-100',
    purple: 'from-purple-500/10 to-purple-600/5 text-purple-600 border-purple-100',
    gray: 'from-slate-500/10 to-slate-600/5 text-slate-600 border-slate-100'
  };
  
  return (
    <div className={`bg-white p-5 rounded-2xl border ${colorMap[color].split(' ')[2]} shadow-sm hover:shadow-md transition-all duration-300 group overflow-hidden relative`}>

      <div className="flex items-center gap-4 relative z-10">
        <div className={`p-3 rounded-xl bg-gradient-to-br ${colorMap[color].split(' ').slice(0, 2).join(' ')} ${colorMap[color].split(' ')[2]}`}>
          <Icon className="h-6 w-6" />
        </div>
        <div>
          <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">{label}</p>
          <p className="text-2xl font-black text-gray-900">{value}</p>
        </div>
      </div>
    </div>
  );
};

// --- Helpers ---

const categoryIcons = {
  'information': Info,
  'event': Calendar,
  'holiday': CalendarOff,
  'exam': BookOpen,
  'meeting': Users,
  'urgent': AlertCircle,
  'Notice': Bell,
  'Message': MessageCircle,
  'Announcement': Bell,
  'Circular': FileText
};

const getCategoryIcon = (category, title = '') => {
  if (categoryIcons[category]) return categoryIcons[category];
  
  const t = title.toLowerCase();
  const c = category.toLowerCase();
  
  if (t.includes('exam') || c.includes('exam')) return BookOpen;
  if (t.includes('holiday') || c.includes('holiday')) return CalendarOff;
  if (t.includes('meeting') || c.includes('meeting')) return Users;
  if (t.includes('urgent') || t.includes('important')) return AlertCircle;
  
  return Bell;
};

const NoticeCategoryIcon = ({ category, title, ...props }) => {
  const Icon = getCategoryIcon(category, title);
  return React.createElement(Icon, props);
};

const NoticeCard = ({ notice, onView, onEdit, onDelete, onSend }) => {
  const isMessage = (notice.type === 'message' || notice.category === 'Message');
  const isSent = notice.status === 'sent';
  const isScheduled = notice.status === 'scheduled' || notice.sendScheduledAt;

  const getRecipientLabel = () => {
    if (notice.sentToSummary) return notice.sentToSummary;
    if (Array.isArray(notice.applicableUnits) && notice.applicableUnits.length > 0) {
      return `${notice.applicableUnits.length} Classes`;
    }
    return 'All School';
  };

  return (
    <div className={`group bg-white rounded-2xl border border-gray-100 p-6 hover:shadow-xl hover:shadow-gray-200/50 transition-all duration-500 relative flex flex-col h-full ${notice.pinned ? 'ring-2 ring-amber-400/30' : ''}`}>
      {notice.pinned && (
        <div className="absolute top-0 right-0 p-3 text-amber-500">
          <Pin className="h-4 w-4 fill-current drop-shadow-sm" />
        </div>
      )}

      <div className="flex justify-between items-start mb-4">
        <div className="flex gap-4 items-center">
          <div className={`h-12 w-12 flex items-center justify-center rounded-xl border-2 shadow-sm ${
            isMessage 
              ? 'bg-emerald-50 border-emerald-100 text-emerald-700' 
              : 'bg-blue-50 border-blue-100 text-blue-700'
          }`}>
            <NoticeCategoryIcon category={notice.category} title={notice.title} size={24} strokeWidth={2.5} />
          </div>
          <div className="min-w-0 pr-4">
            <h3 className="font-bold text-gray-900 line-clamp-1 text-base">{notice.title || 'Important Message'}</h3>
            <div className="flex items-center gap-2 mt-0.5">
              <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-widest ${
                notice.priority === 'High' ? 'bg-red-50 text-red-600' : 
                notice.priority === 'Medium' ? 'bg-amber-50 text-amber-700' : 
                ''
              }`}>
                {notice.category}
              </span>
              {isScheduled && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-widest bg-orange-50 text-orange-600 border border-orange-100">
                  <Clock className="h-3 w-3" /> Scheduled
                </span>
              )}
              <span className="text-[10px] text-gray-400 font-medium">•</span>
              <span className="text-[10px] text-gray-400 font-medium">{new Date(notice.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="flex-1">
        <p className="text-sm text-gray-600 leading-relaxed line-clamp-3 mb-6 font-medium">
          {notice.content}
        </p>
      </div>

      <div className="flex items-center justify-between pt-5 border-t border-gray-50/80">
        <div className="flex flex-col gap-1">
           <div className="flex items-center gap-1.5 text-[11px] font-bold text-gray-400">
            <Users className="h-3.5 w-3.5" /> 
            <span className="truncate max-w-[120px]">{getRecipientLabel()}</span>
          </div>
          {isSent && (
            <div className="flex items-center gap-1 text-[10px] font-bold text-emerald-600">
               <CheckCircle2 className="h-3 w-3" /> SENT SUCCESSFULLY
            </div>
          )}
        </div>

        <div className="flex items-center gap-1">
          {!isSent && (
            <button onClick={onSend} className="p-2 rounded-lg transition-all" style={{ color: 'var(--primary-color, #4f46e5)' }} onMouseOver={(e) => e.currentTarget.style.backgroundColor = 'color-mix(in srgb, var(--primary-color, #4f46e5) 10%, white)'} onMouseOut={(e) => e.currentTarget.style.backgroundColor = ''} title="Send Now">
              <Send className="h-4.5 w-4.5" />
            </button>
          )}
          <button onClick={onView} className="p-2 text-gray-400 hover:bg-gray-50 hover:text-gray-900 rounded-lg transition-all">
            <Eye className="h-4.5 w-4.5" />
          </button>
          <button onClick={onEdit} className="p-2 text-gray-400 hover:bg-gray-50 rounded-lg transition-all" onMouseOver={(e) => e.currentTarget.style.color = 'var(--primary-color, #4f46e5)'} onMouseOut={(e) => e.currentTarget.style.color = ''}>
            <Edit className="h-4.5 w-4.5" />
          </button>
          <button onClick={onDelete} className="p-2 text-gray-400 hover:bg-red-50 hover:text-red-600 rounded-lg transition-all">
            <Trash2 className="h-4.5 w-4.5" />
          </button>
        </div>
      </div>
    </div>
  );
};

// --- Main Page Component ---

export default function NoticeList() {
  const navigate = useNavigate();
  const [notices, setNotices] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // State for Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  
  const [filters, setFilters] = useState({
    applicableUnits: '',
    applicableSections: '',
    category: '',
    status: '',
    page: 1,
    limit: 50
  });

  const { classes: apiClasses } = useData();
  const [selectedClass, setSelectedClass] = useState(null);

  const [classOptions, setClassOptions] = useState([]);
  const [stats, setStats] = useState({ total: 0, sent: 0, scheduled: 0, drafts: 0 });

  // Modal State
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('create');
  const [selectedNotice, setSelectedNotice] = useState(null);
  const [confirmModal, setConfirmModal] = useState({ open: false, type: null, id: null });
  const [isProcessing, setIsProcessing] = useState(false);

  // 1. Initial Load of Classes
  useEffect(() => {
    if (apiClasses?.length > 0) {
      const opts = apiClasses.map(u => ({
        value: u.id || u._id,
        label: u.unitName || u.name,
        sections: u.sections || []
      }));
      setClassOptions(opts);
    }
  }, [apiClasses]);

  useEffect(() => {
    if (filters.applicableUnits) {
      const cls = classOptions.find(c => c.value === filters.applicableUnits);
      setSelectedClass(cls);
    } else {
      setSelectedClass(null);
    }
  }, [filters.applicableUnits, classOptions]);

  // 2. Debounce Search Term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 600); // 600ms debounce
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const calculateStats = (data) => {
    setStats({
      total: data.length,
      sent: data.filter(n => n.status === 'sent').length,
      scheduled: data.filter(n => n.status === 'scheduled').length,
      drafts: data.filter(n => n.status === 'draft').length
    });
  };

  const fetchNotices = async () => {
    setLoading(true);
    try {
      // Construct clean query params
      const params = {};
      
      if (debouncedSearch.trim()) params.search = debouncedSearch.trim();
      if (filters.category) params.category = filters.category;
      
      // Pass status filter to backend
      if (filters.status) params.status = filters.status;
      
      // Pass class filter to backend (applicableUnits)
      if (filters.applicableUnits) {
          params.applicableUnits = [filters.applicableUnits]; 
      }
      
      // Pass section filter to backend (applicableSections)
      if (filters.applicableSections) {
          params.applicableSections = [filters.applicableSections];
      }
      
      // Pagination
      params.page = filters.page;
      params.limit = filters.limit;

      const res = await getNotices(params);
      const data = Array.isArray(res) ? res : res.data || [];
      
      // Sort logic
      const sorted = data.sort((a, b) => {
        if (a.pinned && !b.pinned) return -1;
        if (!a.pinned && b.pinned) return 1;
        return new Date(b.createdAt) - new Date(a.createdAt);
      });

      setNotices(sorted);
      calculateStats(sorted);
    } catch (error) {
      toast.error("Failed to load notices");
    } finally {
      setLoading(false);
    }
  };

  // 3. Master Fetch Effect - Triggered by filters or debounced search
  useEffect(() => {
    fetchNotices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, filters.category, filters.status, filters.applicableUnits, filters.applicableSections]);

  if (loading) {
    return <LoadingOverlay />;
  }

  // --- Handlers ---

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setSearchTerm('');
    setFilters({ applicableUnits: '', category: '', status: '' });
  };

  // CRUD Actions
  const handleDelete = (id) => { 
    setConfirmModal({ open: true, type: 'delete', id });
  };

  const handleSend = (id) => {
    setConfirmModal({ open: true, type: 'send', id });
  };

  const confirmAction = async () => {
    if (!confirmModal.id) return;
    setIsProcessing(true);
    try {
      if (confirmModal.type === 'send') {
        await sendNotice(confirmModal.id);
        toast.success("Notice dispatched successfully!");
        fetchNotices();
      } else if (confirmModal.type === 'delete') {
        await deleteNotice(confirmModal.id); 
        toast.success("Notice deleted");
        fetchNotices();
      }
      setConfirmModal({ open: false, type: null, id: null });
    } catch (err) {
      toast.error(confirmModal.type === 'delete' ? "Delete failed" : "Send failed");
    } finally {
      setIsProcessing(false);
    }
  };

  // Modal handlers
const openCreateModal = () => navigate('/notices/create');
  const openEditModal = (notice) => navigate(`/notices/${notice.id || notice._id}/edit`);
  const openViewModal = (notice) => navigate(`/notices/${notice.id || notice._id}`);
  
  const handleModalSubmit = async (data, action) => {
    // This is no longer used
  };

  return (
    <div className="min-h-screen bg-gray-50/30 p-4 md:p-8 animate-in fade-in duration-500">
      <div className="max-w-7xl mx-auto space-y-6">
        
        {/* Header & Stats */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Bell className="h-7 w-7" style={{ color: 'var(--primary-color, #4f46e5)' }} />
              Notices & Communication
            </h1>
            <p className="text-gray-500 mt-1">Broadcast updates, messages, and circulars.</p>
          </div>
          <BrandedButton
            onClick={openCreateModal}
            variant="primary"
            size="lg"
            className="flex items-center gap-2 shadow-lg min-w-[150px]"
          >
            <Plus className="h-5 w-5" /> New Notice
          </BrandedButton>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard icon={Bell} label="Total" value={stats.total} color="blue" />
          <StatCard icon={CheckCircle2} label="Sent" value={stats.sent} color="green" />
          <StatCard icon={Clock} label="Scheduled" value={stats.scheduled} color="purple" />
          <StatCard icon={FileText} label="Drafts" value={stats.drafts} color="gray" />
        </div>

        {/* Filters Bar */}
        <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm flex flex-col md:flex-row gap-4 justify-between items-center">
          <div className="flex flex-col md:flex-row gap-3 w-full md:w-auto flex-1">
             <div className="relative w-full md:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search notices..."
                className="w-full pl-9 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-100 focus:border-blue-400 transition-all"
                value={searchTerm}
                onChange={handleSearchChange}
              />
            </div>
            
            <div className="flex gap-2 overflow-x-auto pb-2 md:pb-0 hide-scrollbar w-full">
              <select 
                className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-700 font-medium focus:ring-2 focus:ring-blue-100 outline-none"
                value={filters.category}
                onChange={(e) => handleFilterChange('category', e.target.value)}
              >
                <option value="">All Categories</option>
                <option value="Notice">Notice</option>
                <option value="Message">Message</option>
                <option value="Announcement">Announcement</option>
                <option value="Circular">Circular</option>
              </select>

              <select 
                className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-700 font-medium focus:ring-2 focus:ring-blue-100 outline-none max-w-[150px]"
                value={filters.applicableUnits}
                onChange={(e) => {
                  handleFilterChange('applicableUnits', e.target.value);
                  handleFilterChange('applicableSections', ''); // Reset section when class changes
                }}
              >
                <option value="">All Classes</option>
                {classOptions.map(cls => (
                  <option key={cls.value} value={cls.value}>{cls.label}</option>
                ))}
              </select>

              {selectedClass?.sections?.length > 0 && (
                <select 
                  className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-700 font-medium focus:ring-2 focus:ring-blue-100 outline-none max-w-[150px] animate-in slide-in-from-left-2 duration-300"
                  value={filters.applicableSections}
                  onChange={(e) => handleFilterChange('applicableSections', e.target.value)}
                >
                  <option value="">All Sections</option>
                  {selectedClass.sections.map(sec => (
                    <option key={sec._id} value={sec._id}>{sec.name}</option>
                  ))}
                </select>
              )}

              <select 
                className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-700 font-medium focus:ring-2 focus:ring-blue-100 outline-none"
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
              >
                <option value="">All Status</option>
                <option value="sent">Sent</option>
                <option value="draft">Draft</option>
                <option value="scheduled">Scheduled</option>
              </select>
              
              {(searchTerm || filters.category || filters.applicableUnits || filters.applicableSections || filters.status) && (
                <button 
                  onClick={clearFilters}
                  className="px-3 py-2 text-red-600 bg-red-50 hover:bg-red-100 rounded-lg text-sm font-medium transition-colors"
                >
                  Clear
                </button>
              )}
            </div>
          </div>
          
          <button 
            onClick={() => fetchNotices()} 
            className="p-2 text-gray-500 rounded-full transition-all"
            style={{ '--hover-color': 'var(--primary-color, #4f46e5)' }}
            onMouseOver={(e) => {
              e.currentTarget.style.color = 'var(--primary-color, #4f46e5)';
              e.currentTarget.style.backgroundColor = 'color-mix(in srgb, var(--primary-color, #4f46e5) 10%, white)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.color = '';
              e.currentTarget.style.backgroundColor = '';
            }}
            title="Refresh List"
          >
            <RefreshCw className={`h-5 w-5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {notices.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-24 bg-white rounded-xl border border-gray-200 border-dashed">
            <div className="bg-gray-50 p-6 rounded-full mb-4">
              <Bell className="h-10 w-10 text-gray-300" />
            </div>
            <h3 className="text-xl font-bold text-gray-900">No notices found</h3>
            <p className="text-gray-500 mt-2 max-w-md text-center">
              Try adjusting your filters or search query. Or create a new notice to get started.
            </p>
            <button
            onClick={openCreateModal}
            className="mt-6 font-medium"
            style={{ color: 'var(--primary-color, #4f46e5)' }}
            onMouseOver={(e) => e.currentTarget.style.opacity = '0.8'}
            onMouseOut={(e) => e.currentTarget.style.opacity = '1'}
          >
            + Create your first notice
          </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 pb-20">
            {notices.map(notice => (
              <NoticeCard
                key={notice.id || notice._id}
                notice={notice}
                onView={() => openViewModal(notice)}
                onEdit={() => openEditModal(notice)}
                onDelete={() => handleDelete(notice.id || notice._id)}
                onSend={() => handleSend(notice.id || notice._id)}
              />
            ))}
          </div>
        )}
      </div>



      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={confirmModal.open}
        onClose={() => setConfirmModal({ open: false, type: null, id: null })}
        onConfirm={confirmAction}
        title={confirmModal.type === 'delete' ? "Delete Notice" : "Send Notice"}
        message={confirmModal.type === 'delete' ? "Are you sure you want to delete this notice? This action cannot be undone." : "Send this notice now? This may trigger WhatsApp messages to recipients."}
        confirmText={confirmModal.type === 'delete' ? "Delete" : "Send Now"}
        variant={confirmModal.type === 'delete' ? "danger" : "info"}
        isLoading={isProcessing}
      />
    </div>
  );
}
